import React, { useState } from 'react';
import { Stack } from '@mui/material';
import { Add, PostAdd } from '@mui/icons-material';
import Icon from '@/components/Base/Icon';
import { TypesCRM } from '@/types/crm.ts';
import { ColumnModel, CardFilters } from '@/features/crm/types';
import { useUpdateStepCard } from '@/features/crm/api/changeStepCard.ts';
import useModalStore from '@/features/responsible/hooks/useModal.tsx';
import { ColumnWithInfiniteScroll } from '@/features/crm/ColumnWithInfiniteScroll';

type DragAndDropColumnsProps = {
  type: TypesCRM;
  columns: ColumnModel[];
  setOpenModal: (open: boolean) => void;
  filterName?: string;
  filters?: CardFilters;
}

const DragAndDropColumns: React.FC<DragAndDropColumnsProps> = ({ columns, setOpenModal, filterName, filters }) => {
  const {
    type,
  } = useModalStore((state) => state);

  const updateStepCardMutation = useUpdateStepCard()
  const [draggedItem, setDraggedItem] = useState<{ columnId: number; itemId: number } | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Usar diretamente as columns das props, não precisamos de estado local
  const currentColumns = columns;

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, columnId: number, itemId: number) => {
    setDraggedItem({ columnId, itemId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(itemId));
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, targetColumnId: number, targetItemId: number | null) => {
    e.preventDefault();

    if (!draggedItem || isUpdating) return;
    
    // Verifica se é uma movimentação válida
    const sourceColumnIndex = currentColumns.findIndex((col) => col.id === draggedItem.columnId);
    const targetColumnIndex = currentColumns.findIndex((col) => col.id === targetColumnId);
    const draggedItemIndex = currentColumns[sourceColumnIndex]?.cards?.findIndex((item) => item.id === draggedItem.itemId);

    if (sourceColumnIndex === -1 || targetColumnIndex === -1 || draggedItemIndex === -1) return;

    setIsUpdating(true);

    try {
      // Chama a API - o React Query vai atualizar automaticamente o cache no onSuccess
      await updateStepCardMutation.mutateAsync({
        id: draggedItem.itemId,
        type,
        stepId: targetColumnId
      });

      // Não precisamos atualizar o estado local aqui, pois o React Query já faz isso
      // O useEffect vai sincronizar com os novos dados vindos do cache
    } catch (error) {
      // Em caso de erro, não atualiza a UI e mantém o estado original
      console.error('Erro ao mover card:', error);
    } finally {
      setIsUpdating(false);
    }

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const showAddColumn = false;

  return (
    <div style={{ display: 'flex', marginTop: '20px', gap: '20px', overflowX: 'auto', scrollbarWidth: 'thin' }}>
      {columns.map((column) => {
        return (
          <div
            key={column.id}
            style={{
              border: '1px solid #ddd',
              borderRadius: '5px',
              padding: '10px',
              minWidth: '300px',
              maxHeight: 'calc(100vh - 250px)',
              backgroundColor: '#FFF',
              display: 'flex',
              flexDirection: 'column'
            }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, column.id, null)}
          >
            <Stack flexDirection={'row'} justifyContent={'space-between'} mb={1}>
              <p style={{ fontSize: '16px', fontWeight: 600 }}>{column.nome}</p>
              <Icon name={column.icon} />
            </Stack>

            <ColumnWithInfiniteScroll
              column={column}
              draggedItem={draggedItem}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDrop={handleDrop}
              handleDragEnd={handleDragEnd}
              filterName={filterName}
              isUpdating={isUpdating}
              filters={filters}
            />
          </div>
        );
      })}

      {
        showAddColumn && (
          <div
            style={{
              borderRadius: '5px',
              padding: '10px',
              minWidth: '215px',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', color: '#646570', fontWeight: 500 }}>
              <Add />
              <p onClick={() => setOpenModal(true)}>Adicionar coluna</p>
              <PostAdd />
            </div>
          </div>
        )
      }
    </div>
  );
};

export default DragAndDropColumns;
